import { createElement, type ReactNode, type ComponentType, type FunctionComponent } from "react";
import {
  StickyNote,
  FileText,
  BarChart,
  Star,
  Zap,
  Circle,
  Square,
  Settings,
  Users,
  Shield,
  Bell,
  type LucideProps,
} from "lucide-react";
import { createRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import type { AnyRoute } from "@tanstack/react-router";
import { FeatureGate } from "./feature-flags/FeatureGate";
import type { NavItem, NavSection } from "@/core/layout/types";
import type {
  PluginClientManifest,
  PluginClientNavItem,
  PluginGlobalComponent,
  PluginTopbarAction,
} from "./types";

/**
 * Explicit map of Lucide icon names to components.
 * This avoids importing the entire lucide-react library (~1000+ icons)
 * which prevents tree-shaking when accessed by dynamic string key.
 * Add new entries here as plugins reference additional icons.
 */
const ICON_MAP: Record<string, ComponentType<LucideProps>> = {
  StickyNote,
  FileText,
  BarChart,
  Star,
  Zap,
  Circle,
  Square,
  Settings,
  Users,
  Shield,
  Bell,
};

/**
 * Resolve a Lucide icon name string (e.g., "FileText") to a React element.
 * Falls back to a generic square icon if the name is not in the map.
 */
function resolveIcon(iconName: string): ReactNode {
  const IconComponent = ICON_MAP[iconName];
  if (IconComponent) {
    return createElement(IconComponent, { className: "size-4" });
  }
  // Fallback to a generic icon
  return createElement(Square, { className: "size-4" });
}

/**
 * Translation function shape compatible with react-i18next's `t`: it resolves a
 * key, optionally within an explicit i18n namespace via `{ ns }`. The identity
 * default used for SSR/test contexts ignores the options object.
 */
type NavTFunction = (key: string, options?: { ns?: string }) => string;

/**
 * Convert a plugin nav item to a layout NavItem (resolving icon names and i18n label).
 *
 * @param item - Plugin nav item with a labelKey (i18n key) and icon name
 * @param t - Translation function that resolves the labelKey to a display string
 * @param ns - The plugin's first i18n namespace, if any. When present the label
 *             is resolved in-namespace (`t(labelKey, { ns })`) so a key like
 *             "nav.title" resolves against the plugin's bundle instead of the
 *             default (core) namespace, which would yield the raw key (D-05).
 */
function toNavItem(item: PluginClientNavItem, t: NavTFunction, ns?: string): NavItem {
  return {
    label: ns ? t(item.labelKey, { ns }) : t(item.labelKey),
    href: item.href,
    icon: resolveIcon(item.icon),
    badge: item.badge,
  };
}

/**
 * Build navigation sections from enabled plugin manifests.
 *
 * Groups plugin nav items by section, sorts by position within each section,
 * and breaks ties alphabetically by plugin name.
 * Core sections always take precedence -- these are appended after core sections.
 *
 * @param plugins - Plugin manifests to build nav sections from
 * @param enabledFlags - Set of enabled feature flag keys
 * @param t - Translation function to resolve nav item labelKey values. Defaults to identity
 *            (returns the key as-is) when not provided, which is safe for tests/SSR contexts.
 */
export function buildPluginNavSections(
  plugins: PluginClientManifest[],
  enabledFlags: Set<string>,
  t: NavTFunction = (key) => key,
): NavSection[] {
  const sectionMap = new Map<string, { position: number; items: NavItem[] }[]>();

  for (const plugin of plugins) {
    if (!enabledFlags.has(plugin.featureFlagKey)) continue;
    if (!plugin.navigation) continue;

    const sectionName = plugin.navigation.section ?? "plugins";
    if (!sectionMap.has(sectionName)) {
      sectionMap.set(sectionName, []);
    }

    // Resolve each nav item's label in the plugin's first declared i18n
    // namespace so e.g. notes' "nav.title" resolves against the "notes" bundle,
    // not the core default namespace (which would render the raw key — D-05).
    const ns = plugin.i18nNamespaces?.[0];
    sectionMap.get(sectionName)!.push({
      position: plugin.navigation.position,
      items: plugin.navigation.items.map((item) => toNavItem(item, t, ns)),
    });
  }

  const sections: NavSection[] = [];
  for (const [sectionName, entries] of sectionMap) {
    // Sort by position, then alphabetically by first item label for ties
    entries.sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position;
      const aLabel = a.items[0]?.label ?? "";
      const bLabel = b.items[0]?.label ?? "";
      return aLabel.localeCompare(bLabel);
    });

    const items = entries.flatMap((e) => e.items);
    sections.push({
      label: sectionName,
      items,
    });
  }

  return sections;
}

/**
 * Build TanStack Router routes from plugin manifests.
 *
 * All plugin routes are registered unconditionally regardless of feature flag state.
 * TanStack Router builds its route tree at module-evaluation time, before async
 * feature flag evaluations complete. Runtime gating is therefore per-render:
 * when `opts.flagFallback` is supplied (router.tsx passes
 * FeatureNotAvailablePage), each route component is wrapped in a FeatureGate on
 * the plugin's flag, so a flag-OFF direct URL renders the fallback page instead
 * of a blank screen. Containers keep their own inner FeatureGate as
 * defense-in-depth; the route-level gate is what the user sees.
 *
 * The `enabledFlags` parameter is kept in the signature so callers can pass the
 * set of all plugin flag keys (as router.tsx does), making the intent explicit
 * at the call site without the function needing to look up flags itself.
 */
export function withFlagFallback(
  flagKey: string,
  Component: ComponentType,
  Fallback: ComponentType,
): FunctionComponent<Record<string, unknown>> {
  // Props are forwarded verbatim: TanStack route components read their data via
  // hooks today, but the wrapper must stay transparent if that ever changes.
  return function FlagGatedRoute(props: Record<string, unknown>) {
    // FeatureGateProps declares children required, so createElement's generic
    // only accepts them inside the props object; this .ts module has no JSX
    // form to use instead — hence the targeted rule suppression.
    // eslint-disable-next-line react/no-children-prop
    return createElement(FeatureGate, {
      flag: flagKey,
      fallback: createElement(Fallback),
      children: createElement(Component, props),
    });
  };
}

export function buildPluginRoutes(
  parentRoute: AnyRoute,
  plugins: PluginClientManifest[],
  // Kept for call-site explicitness; per-render gating uses opts.flagFallback.
  _enabledFlags: Set<string>,
  opts?: { flagFallback?: ComponentType },
): AnyRoute[] {
  const routes: AnyRoute[] = [];

  for (const plugin of plugins) {
    if (!plugin.routes) continue;

    for (const routeDef of plugin.routes) {
      const lazy = lazyRouteComponent(routeDef.component);
      const component = opts?.flagFallback
        ? withFlagFallback(plugin.featureFlagKey, lazy, opts.flagFallback)
        : lazy;
      routes.push(
        createRoute({
          getParentRoute: () => parentRoute,
          path: routeDef.path,
          // Defense-in-depth client RBAC guard (09b, WR-01). Relocated `/admin/*`
          // plugin routes lost `adminLayoutRoute`'s `beforeLoad` when they moved
          // out of router.tsx and under `authenticatedRoute` (which only checks
          // `isAuthenticated`). Re-inject the SAME redirect from the manifest so a
          // logged-in non-admin is bounced to /dashboard instead of rendering the
          // admin container. Server RBAC (adminProcedure/superAdminProcedure) is
          // still the authoritative DATA gate — this is UX defense-in-depth only.
          // Optional chaining: if the auth context is ever absent (initial
          // routing setup, SSR, or an under-mocked test), fail SAFE — treat the
          // user as unprivileged and redirect, never throw a TypeError.
          beforeLoad: routeDef.requiresSuperAdmin
            ? ({ context }: { context: { auth?: { isSuperAdmin?: boolean } } }) => {
                if (!context?.auth?.isSuperAdmin) {
                  throw redirect({ to: "/dashboard" });
                }
              }
            : routeDef.requiresAdmin
              ? ({
                  context,
                }: {
                  context: { auth?: { isAdmin?: boolean; isSuperAdmin?: boolean } };
                }) => {
                  if (!context?.auth?.isAdmin && !context?.auth?.isSuperAdmin) {
                    throw redirect({ to: "/dashboard" });
                  }
                }
              : undefined,
          component,
          staticData: {
            pluginId: plugin.id,
            requiresAuth: true,
            requiresAdmin: routeDef.requiresAdmin,
            requiresSuperAdmin: routeDef.requiresSuperAdmin,
          },
        }),
      );
    }
  }

  return routes;
}

/**
 * Build PUBLIC (session-less) TanStack Router routes from plugin manifests
 * (09b-01, D-01/D-05).
 *
 * Modeled EXACTLY on `buildPluginRoutes`, but sourced from each manifest's
 * `publicRoutes[]` channel and mounted under a session-less parent (rootRoute)
 * by the caller. The critical difference: `staticData` deliberately does NOT set
 * `requiresAuth: true` — these routes render without a session (e.g. the public
 * `/pricing` catalog for unauthenticated visitors). Runtime flag gating, if any,
 * is handled inside each container (which degrades gracefully when its flag is
 * off), mirroring the authenticated path.
 *
 * Keeping this separate from `buildPluginRoutes` (which mounts under
 * `authenticatedRoute` and redirects to /login) lets a public plugin route live
 * in the owning plugin's manifest and strip cleanly with the plugin.
 */
export function buildPluginPublicRoutes(
  parentRoute: AnyRoute,
  plugins: PluginClientManifest[],
): AnyRoute[] {
  const routes: AnyRoute[] = [];

  for (const plugin of plugins) {
    if (!plugin.publicRoutes) continue;

    for (const routeDef of plugin.publicRoutes) {
      routes.push(
        createRoute({
          getParentRoute: () => parentRoute,
          path: routeDef.path,
          component: lazyRouteComponent(routeDef.component),
          staticData: {
            pluginId: plugin.id,
          },
        }),
      );
    }
  }

  return routes;
}

/**
 * Build the list of TopBar actions contributed by enabled plugins.
 *
 * Mirrors `buildPluginNavSections`' gating: an action is only included when its
 * owning plugin's `featureFlagKey` is in `enabledFlags`. The CORE TopBar renders
 * the returned actions generically (review HIGH #3) — it NEVER imports the
 * plugin component itself, so a new plugin can contribute a TopBar action with
 * no core edit. Per-manifest action order is preserved.
 *
 * @param plugins - Plugin manifests to collect topbar actions from
 * @param enabledFlags - Set of enabled feature flag keys
 */
export function buildTopbarActions(
  plugins: PluginClientManifest[],
  enabledFlags: Set<string>,
): PluginTopbarAction[] {
  const actions: PluginTopbarAction[] = [];
  for (const plugin of plugins) {
    if (!enabledFlags.has(plugin.featureFlagKey)) continue;
    if (!plugin.topbarActions) continue;
    actions.push(...plugin.topbarActions);
  }
  return actions;
}

/**
 * Collect app-wide components from enabled plugin manifests (the consent
 * banner et al.). Identical gating to `buildTopbarActions`: only plugins whose
 * `featureFlagKey` is enabled contribute, so a disabled plugin's chrome never
 * mounts (and never fires its queries). The shell renders these generically —
 * core never imports the plugin component (the topbarActions isolation seam,
 * applied app-wide). Per-manifest order is preserved.
 */
export function buildGlobalComponents(
  plugins: PluginClientManifest[],
  enabledFlags: Set<string>,
): PluginGlobalComponent[] {
  const components: PluginGlobalComponent[] = [];
  for (const plugin of plugins) {
    if (!enabledFlags.has(plugin.featureFlagKey)) continue;
    if (!plugin.globalComponents) continue;
    components.push(...plugin.globalComponents);
  }
  return components;
}

/**
 * Get the list of all feature flag keys from registered plugin manifests.
 */
export function getPluginFlagKeys(plugins: PluginClientManifest[]): string[] {
  return plugins.map((p) => p.featureFlagKey);
}
